import { useDisclosure } from "@chakra-ui/hooks";
import AtriumApi, { getApiErrorMessage } from "api";
import Badge from "components/badge";
import Button from "components/button";
import Card from "components/card";
import ConfirmDialog from "components/dialog/ConfirmDialog";
import { InputField, Select } from "components/fields";
import Skeleton from "components/skeleton/Skeleton";
import { useEffect, useId, useState } from "react";
import { MdExpandMore } from "react-icons/md";
import notify from "lib/notify";
import { track } from "lib/mixpanel";
import { EVENTS } from "lib/events";
import { EMPTY, formatDateTime } from "utils/format";
import { SettingsFormActions, SettingsSectionSkeleton, SettingsSubsection } from "./components/SettingsSection";
import SettingToggleRow, { AutosaveHint } from "./components/SettingToggleRow";

const PlanningCenterLogo = () => {
  return (
    <svg
      className="inline-block"
      width="28"
      height="30"
      viewBox="0 0 28 30"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_690_12969)">
        <path
          d="M24.7852 3.27941C26.7556 3.79412 28 5.64706 28 7.60294V21.2941C28 23.25 26.6519 25 24.7852 25.6176L16.2815 28.1912C14.8296 28.6029 13.1704 28.6029 11.7185 28.1912L3.21481 25.7206C1.34815 25.1029 0 23.25 0 21.2941V7.60294C0 5.64706 1.34815 3.89706 3.21481 3.27941L11.7185 0.808824C13.1704 0.397059 14.8296 0.397059 16.2815 0.808824L24.7852 3.27941Z"
          fill="#2565F4"
        />
        <path
          d="M18.5 9.30005L13.7 10.7001C13.5 10.7001 13.4 10.8001 13.2 10.7001L8.4 9.30005C7.4 9.00005 6.5 9.70005 6.5 10.7001V21.5001C6.5 21.7001 6.6 21.9001 6.8 21.9001L8.6 22.4001C8.9 22.5001 9.1 22.3001 9.1 22.0001V19.5001C9.1 19.5001 9.1 19.4001 9.2 19.4001L12.8 20.5001C13.3 20.6001 13.8 20.6001 14.2 20.5001L19.6 18.8001C20.2 18.7001 20.5 18.3001 20.5 17.8001V10.8001C20.5 9.80005 19.5 9.00005 18.5 9.30005ZM17.9 16.5001C17.9 16.6001 17.8 16.7001 17.7 16.7001L13.8 17.9001C13.6 18.0001 13.4 18.0001 13.2 17.9001L9.4 16.8001C9.3 16.8001 9.2 16.7001 9.2 16.6001V12.6001C9.2 12.5001 9.3 12.4001 9.5 12.4001L12.6 13.3001C13.2 13.5001 13.9 13.5001 14.5 13.3001L17.6 12.4001C17.7 12.4001 17.9 12.5001 17.9 12.6001V16.5001Z"
          fill="white"
        />
      </g>
      <defs>
        <clipPath id="clip0_690_12969">
          <rect
            width="28"
            height="29"
            fill="white"
            transform="translate(0 0.5)"
          />
        </clipPath>
      </defs>
    </svg>
  );
};

const ChurchMetricsLogo = () => {
  return (
    <svg
      className="inline-block"
      width="28"
      height="28"
      viewBox="0 0 204 192"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M0 0 C67.32 0 134.64 0 204 0 C204 63.36 204 126.72 204 192 C136.68 192 69.36 192 0 192 C0 128.64 0 65.28 0 0 Z " fill="#4CB239" transform="translate(0,0)"/>
      <path d="M0 0 C4.39749032 0.37266867 4.90093585 1.06497989 7.6875 4.3125 C8.92506864 6.10134151 10.11092654 7.92663376 11.25390625 9.77734375 C11.90423828 10.82470703 12.55457031 11.87207031 13.22460938 12.95117188 C14.22717773 14.5840918 14.22717773 14.5840918 15.25 16.25 C15.92224609 17.33474609 16.59449219 18.41949219 17.28710938 19.53710938 C19.09477388 22.45773514 20.89674576 25.38147804 22.6875 28.3125 C23.25331787 29.19494385 23.81913574 30.0773877 24.40209961 30.98657227 C25.6875 33.3125 25.6875 33.3125 25.6875 36.125 C24.36565636 40.33837661 22.1771179 43.69290792 19.6875 47.3125 C15.70675721 45.98558574 15.25599696 44.34573596 13.18359375 40.74609375 C12.52552734 39.62138672 11.86746094 38.49667969 11.18945312 37.33789062 C10.50041704 36.14243677 9.81227662 34.94646626 9.125 33.75 C7.77799464 31.41016559 6.42126371 29.07694139 5.05859375 26.74609375 C4.45974365 25.70509521 3.86089355 24.66409668 3.24389648 23.59155273 C2.47348022 22.46342163 2.47348022 22.46342163 1.6875 21.3125 C1.0275 21.3125 0.3675 21.3125 -0.3125 21.3125 C-2.02805 23.88582499 -3.62167865 26.46184492 -5.1875 29.125 C-6.17588228 30.7728564 -7.16550909 32.41996685 -8.15625 34.06640625 C-8.671875 34.92637207 -9.1875 35.78633789 -9.71875 36.67236328 C-12.32973079 40.99760014 -15.00114841 45.28378425 -17.6875 49.5625 C-18.17460449 50.34729736 -18.66170898 51.13209473 -19.16357422 51.94067383 C-20.51384791 54.08845726 -21.90077022 56.20473055 -23.3125 58.3125 C-23.7137207 58.94349609 -24.11494141 59.57449219 -24.52832031 60.22460938 C-25.7265625 61.921875 -25.7265625 61.921875 -28.3125 64.3125 C-31.8046875 64.62890625 -31.8046875 64.62890625 -35.3125 63.3125 C-37.46239221 61.21082829 -38.9159803 58.82968708 -40.4375 56.25 C-41.07534424 55.22869995 -41.07534424 55.22869995 -41.72607422 54.18676758 C-42.56254095 52.84325902 -43.38986413 51.49400569 -44.20703125 50.13867188 C-45.5668068 47.89239955 -47.00372448 45.69954198 -48.4375 43.5 C-50.3125 40.3125 -50.3125 40.3125 -50.3125 38.3125 C-53.47849065 41.31903193 -55.61207567 44.79667207 -57.875 48.5 C-58.7759218 49.95840739 -59.67696673 51.41673872 -60.578125 52.875 C-61.03509766 53.6175 -61.49207031 54.36 -61.96289062 55.125 C-64.39752805 59.07115617 -66.86651907 62.99584685 -69.33227539 66.92260742 C-71.83978914 70.92610613 -74.31146888 74.94944485 -76.76171875 78.98828125 C-77.35041748 79.95354736 -77.93911621 80.91881348 -78.5456543 81.91333008 C-79.71063247 83.82491165 -80.86979467 85.74005154 -82.02270508 87.65893555 C-82.5514624 88.52429932 -83.08021973 89.38966309 -83.625 90.28125 C-84.09550781 91.05919922 -84.56601563 91.83714844 -85.05078125 92.63867188 C-85.46714844 93.19103516 -85.88351563 93.74339844 -86.3125 94.3125 C-86.9725 94.3125 -87.6325 94.3125 -88.3125 94.3125 C-91.66809194 90.4443431 -94.78079444 86.52445104 -95.3125 81.3125 C-93.67729683 75.39339101 -89.50250657 70.3981883 -85.91259766 65.52197266 C-83.61530323 62.3497855 -81.59718361 59.03134132 -79.5625 55.6875 C-78.75449053 54.39595336 -77.94450158 53.10564357 -77.1328125 51.81640625 C-74.90709274 48.26921672 -72.69822066 44.71180807 -70.4921875 41.15234375 C-69.8228418 40.07484863 -69.15349609 38.99735352 -68.46386719 37.88720703 C-67.13654672 35.750381 -65.81158641 33.6120867 -64.48925781 31.47216797 C-63.87663086 30.48748535 -63.26400391 29.50280273 -62.6328125 28.48828125 C-62.08898926 27.61083252 -61.54516602 26.73338379 -60.98486328 25.8293457 C-56.22147313 18.66061737 -56.22147313 18.66061737 -52.375 17.1875 C-49.3125 17.3125 -49.3125 17.3125 -46.94140625 19.3203125 C-43.90361062 22.77789267 -41.5588301 26.38203848 -39.1875 30.3125 C-38.75824219 31.01117188 -38.32898438 31.70984375 -37.88671875 32.4296875 C-35.67117855 36.04272229 -33.48677733 39.67449349 -31.3125 43.3125 C-26.63813474 36.41846409 -22.2027043 29.39004147 -17.8125 22.3125 C-4.21892658 0.39801194 -4.21892658 0.39801194 0 0 Z " fill="#FAFCFA" transform="translate(136.3125,49.6875)"/>
    </svg>
  );
};

/**
 * Collapsible integration card: the heading is a disclosure button (logo, name,
 * summary and connection status); the body holds the integration's subsections.
 */
const IntegrationPanel = ({ logo, title, description, connected, expanded, onToggle, children }) => {
  const id = useId();
  const titleId = `${id}-title`;
  const panelId = `${id}-panel`;

  return (
    <Card as="section" aria-labelledby={titleId}>
      <h2>
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls={panelId}
          onClick={onToggle}
          className="focus-ring flex w-full items-start gap-4 rounded-card p-6 text-left"
        >
          <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-control bg-surface-subtle">
            {logo}
          </span>
          <span className="min-w-0 flex-1">
            <span id={titleId} className="text-heading block">
              {title}
            </span>
            <span className="mt-1 block text-sm font-normal text-fg-muted">{description}</span>
          </span>
          <span className="flex shrink-0 items-center gap-2">
            <Badge tone={connected ? "success" : "neutral"} dot>
              {connected ? "Connected" : "Not Connected"}
            </Badge>
            <MdExpandMore
              aria-hidden="true"
              className={`h-6 w-6 text-fg-muted transition-transform ${expanded ? "rotate-180" : ""}`}
            />
          </span>
        </button>
      </h2>
      {expanded && (
        <div id={panelId} className="border-t border-line p-6">
          {children}
        </div>
      )}
    </Card>
  );
};

const SyncGroup = ({ title, children }) => {
  const id = useId();
  return (
    <div role="group" aria-labelledby={id} className="[&:not(:first-child)]:mt-6">
      <h4 id={id} className="mb-2 text-xs font-semibold uppercase tracking-wide text-fg-muted">
        {title}
      </h4>
      {children}
    </div>
  );
};

const ManualSyncSection = ({ lastSyncAt, stats, loading, onSync }) => (
  <SettingsSubsection
    title="Manual Sync"
    description="Your data syncs automatically every 10 minutes. Use Sync Now to start a sync right away."
    action={
      <Button variant="secondary" loading={loading} onClick={onSync}>
        {loading ? "Syncing…" : "Sync Now"}
      </Button>
    }
  >
    <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
      {[
        { label: "Last Sync", value: lastSyncAt ? formatDateTime(lastSyncAt) : "No sync history yet" },
        ...stats,
      ].map(({ label, value }) => (
        <div key={label}>
          <dt className="text-fg-muted">{label}</dt>
          <dd className="mt-0.5 font-medium text-fg">{value ?? EMPTY}</dd>
        </div>
      ))}
    </dl>
  </SettingsSubsection>
);

/**
 * Disconnect row plus its confirmation. `onDisconnect` resolves true on success.
 */
const DisconnectSection = ({ integration, description, onDisconnect }) => {
  const disclosure = useDisclosure();
  const [disconnecting, setDisconnecting] = useState(false);

  const handleConfirm = async () => {
    setDisconnecting(true);
    try {
      if (await onDisconnect()) disclosure.onClose();
    } finally {
      setDisconnecting(false);
    }
  };

  return (
    <SettingsSubsection
      title="Disconnect"
      description={description}
      action={
        <Button variant="secondary" onClick={disclosure.onOpen}>
          Disconnect
        </Button>
      }
    >
      <ConfirmDialog
        isOpen={disclosure.isOpen}
        title={`Disconnect ${integration}?`}
        message="Syncing will stop. Your existing data will be preserved."
        confirmLabel="Disconnect"
        tone="danger"
        loading={disconnecting}
        onConfirm={handleConfirm}
        onCancel={disclosure.onClose}
      />
    </SettingsSubsection>
  );
};

const PlanningCenterCard = ({ integrationStatus, onStatusChange }) => {
  const [expanded, setExpanded] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [manualSyncLoading, setManualSyncLoading] = useState(false);
  const [connectLoading, setConnectLoading] = useState(false);

  const [syncSettings, setSyncSettings] = useState({
    campuses: false,
    people: false,
    households: false,
    contactInfo: false,
    volunteerRoles: false,
    checkins: false,
    attendanceRecords: false,
    serviceTemplate: false,
  });

  const [lastSync, setLastSync] = useState({});

  const isConnected = !!integrationStatus?.planningCenter?.isAccessTokenSet;

  useEffect(() => {
    if (expanded && isConnected) {
      fetchSyncSettings();
      fetchLastSync();
    }
  }, [expanded, isConnected]);

  const fetchSyncSettings = async () => {
    try {
      setSyncLoading(true);
      const response = await AtriumApi.get("/v1/sync");
      if (response) {
        setSyncSettings({
          campuses: response?.data?.sync?.campuses || false,
          people: response?.data?.sync?.people || false,
          households: response?.data?.sync?.households || false,
          contactInfo: response?.data?.sync?.contactInfo || false,
          volunteerRoles: response?.data?.sync?.volunteerRoles || false,
          checkins: response?.data?.sync?.checkins || false,
          attendanceRecords: response?.data?.sync?.attendanceRecords || false,
          serviceTemplate: response?.data?.sync?.serviceTemplate || false,
        });
      }
    } catch (error) {
      console.error("Failed to fetch sync settings:", error);
    } finally {
      setSyncLoading(false);
    }
  };

  const fetchLastSync = async () => {
    try {
      const response = await AtriumApi.get("/v1/last-total-sync-data");
      if (response) {
        setLastSync(response?.data?.church || {});
      }
    } catch (error) {
      console.error("Failed to fetch last sync data:", error);
    }
  };

  const handleConnect = async () => {
    setConnectLoading(true);
    try {
      const response = await AtriumApi.get("/v1/planning-center/oauth-url");
      if (response?.data?.authUrl) {
        track(EVENTS.SETTINGS_INTEGRATION_CONNECTED, { integration_name: "planning_center" });
        window.location.href = response.data.authUrl;
      } else {
        notify.error("Planning Center integration is not configured on this server.");
      }
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not start the Planning Center connection"));
    } finally {
      setConnectLoading(false);
    }
  };

  const handleToggleSync = async (field) => {
    const newSettings = {
      ...syncSettings,
      [field]: !syncSettings[field],
    };
    setSyncSettings(newSettings);

    try {
      await AtriumApi.post("/v1/sync", newSettings);
      notify.success("Sync setting saved");
    } catch (error) {
      setSyncSettings((prev) => ({ ...prev }));
      notify.error(getApiErrorMessage(error, `Could not update the ${field} sync setting`));
    }
  };

  const handleManualSync = async () => {
    try {
      setManualSyncLoading(true);
      await AtriumApi.post("/v1/manual-sync", { source: "pc" });
      notify.success("Sync started. It may take a few minutes and runs in the background.");
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not sync data"));
    } finally {
      setManualSyncLoading(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await AtriumApi.delete("/v1/disconnect");
      notify.success("Disconnected from Planning Center");
      onStatusChange();
      return true;
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not disconnect from Planning Center"));
      return false;
    }
  };

  const syncRow = (field, label) => (
    <SettingToggleRow
      label={label}
      checked={syncSettings[field]}
      onChange={() => handleToggleSync(field)}
    />
  );

  return (
    <IntegrationPanel
      logo={<PlanningCenterLogo />}
      title="Planning Center"
      description="Organize information, coordinate events, communicate with your team and connect with your congregation."
      connected={isConnected}
      expanded={expanded}
      onToggle={() => setExpanded((prev) => !prev)}
    >
      {!isConnected && (
        <SettingsSubsection
          title="Connect"
          description="Connect your Planning Center account to sync people, campuses and households with Atrium. Make sure you are signed in to the correct Planning Center account in this browser before connecting."
        >
          <Button onClick={handleConnect} loading={connectLoading}>
            {connectLoading ? "Redirecting…" : "Connect to Planning Center"}
          </Button>
        </SettingsSubsection>
      )}

      {isConnected && (
        <>
          <SettingsSubsection
            title="Sync Configuration"
            description="Choose which data to sync with Atrium."
          >
            {syncLoading ? (
              <Skeleton lines={6} />
            ) : (
              <>
                <SyncGroup title="User Data">
                  {syncRow("campuses", "Sync campuses")}
                  {syncRow("people", "Sync people")}
                  {syncRow("households", "Sync households")}
                  {syncRow("contactInfo", "Sync demographics and contact information")}
                  <SettingToggleRow
                    label="Sync family relationships"
                    description="Coming soon"
                    checked={false}
                    disabled
                    onChange={() => {}}
                  />
                </SyncGroup>
                <SyncGroup title="Services and Events">
                  {syncRow("serviceTemplate", "Import service templates (sermon formats, event schedules)")}
                  {syncRow("volunteerRoles", "Sync volunteer role assignments within events")}
                </SyncGroup>
                <SyncGroup title="Attendance Data">
                  {syncRow("attendanceRecords", "Sync attendance records")}
                  {syncRow("checkins", "Import event check-ins for mapping attendance data")}
                </SyncGroup>
                <AutosaveHint />
              </>
            )}
          </SettingsSubsection>

          <ManualSyncSection
            lastSyncAt={lastSync?.updatedAt}
            stats={[
              { label: "Current Update Count", value: lastSync?.totalSyncCount },
              { label: "Duplicate Records Found", value: lastSync?.duplicateCount },
            ]}
            loading={manualSyncLoading}
            onSync={handleManualSync}
          />

          <DisconnectSection
            integration="Planning Center"
            description="Disconnect the integration and stop syncing. Existing data will be preserved."
            onDisconnect={handleDisconnect}
          />
        </>
      )}
    </IntegrationPanel>
  );
};

const SYNC_FREQUENCY_OPTIONS = [
  { value: "daily", label: "Daily (2:00 AM)" },
  { value: "weekly", label: "Weekly (Sunday 2:00 AM)" },
  { value: "monthly", label: "Monthly (1st of month 2:00 AM)" },
];

const ChurchMetricsCard = ({ integrationStatus, onStatusChange }) => {
  const [expanded, setExpanded] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [manualSyncLoading, setManualSyncLoading] = useState(false);
  const [connectLoading, setConnectLoading] = useState(false);
  const [credentials, setCredentials] = useState({ email: "", apiKey: "" });
  const [credErrors, setCredErrors] = useState({ email: "", apiKey: "" });
  const [lastSync, setLastSync] = useState({});

  const [syncSettings, setSyncSettings] = useState({
    attendanceRecords: false,
    serviceRecords: false,
    visitorData: false,
    growthMetrics: false,
    autoSync: false,
    syncFrequency: "daily",
  });

  const isConnected = !!integrationStatus?.churchMetrics?.connected;

  useEffect(() => {
    if (expanded && isConnected) {
      fetchSyncSettings();
      fetchLastSync();
    }
  }, [expanded, isConnected]);

  const fetchSyncSettings = async () => {
    try {
      setSyncLoading(true);
      const response = await AtriumApi.get("/v1/church-metrics/sync-settings");
      if (response) {
        setSyncSettings({
          attendanceRecords: response?.data?.settings?.attendanceRecords || false,
          serviceRecords: response?.data?.settings?.serviceRecords || false,
          visitorData: response?.data?.settings?.visitorData || false,
          growthMetrics: response?.data?.settings?.growthMetrics || false,
          autoSync: response?.data?.settings?.autoSync || false,
          syncFrequency: response?.data?.settings?.syncFrequency || "daily",
        });
      }
    } catch (error) {
      console.error("Failed to fetch Church Metrics sync settings:", error);
    } finally {
      setSyncLoading(false);
    }
  };

  const fetchLastSync = async () => {
    try {
      const response = await AtriumApi.get("/v1/church-metrics/last-sync");
      if (response) {
        setLastSync(response?.data?.sync || {});
        if (response?.data?.sync?.syncInProgress) {
          setManualSyncLoading(true);
        }
      }
    } catch (error) {
      console.error("Failed to fetch Church Metrics last sync:", error);
    }
  };

  const validateCredentials = () => {
    const newErrors = { email: "", apiKey: "" };
    let isValid = true;

    if (!credentials.email.trim()) {
      newErrors.email = "Email is required";
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(credentials.email)) {
      newErrors.email = "Email is invalid";
      isValid = false;
    }

    if (!credentials.apiKey.trim()) {
      newErrors.apiKey = "API Key is required";
      isValid = false;
    }

    setCredErrors(newErrors);
    return isValid;
  };

  const handleConnect = async (event) => {
    event.preventDefault();
    if (!validateCredentials()) return;

    setConnectLoading(true);
    try {
      await AtriumApi.post("/v1/church-metrics/connect", credentials);
      notify.success("Connected to Church Metrics");
      setCredentials({ email: "", apiKey: "" });
      onStatusChange();
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not connect to Church Metrics"));
    } finally {
      setConnectLoading(false);
    }
  };

  const handleToggleSync = async (field) => {
    const newSettings = { ...syncSettings, [field]: !syncSettings[field] };
    setSyncSettings(newSettings);
    try {
      await AtriumApi.post("/v1/church-metrics/sync-settings", newSettings);
      notify.success("Sync setting saved");
    } catch (error) {
      setSyncSettings((prev) => ({ ...prev }));
      notify.error(getApiErrorMessage(error, `Could not update the ${field} sync setting`));
    }
  };

  const handleFrequencyChange = async (value) => {
    const newSettings = { ...syncSettings, syncFrequency: value };
    setSyncSettings(newSettings);
    try {
      await AtriumApi.post("/v1/church-metrics/sync-settings", newSettings);
      notify.success("Sync frequency saved");
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not update sync frequency"));
    }
  };

  const handleManualSync = async () => {
    try {
      setManualSyncLoading(true);
      await AtriumApi.post("/v1/church-metrics/manual-sync");
      notify.success("Sync started. Data will be synced in the background.");
      const pollInterval = setInterval(async () => {
        const response = await AtriumApi.get("/v1/church-metrics/last-sync").catch(() => null);
        const syncData = response?.data?.sync;
        if (syncData && !syncData.syncInProgress) {
          clearInterval(pollInterval);
          setManualSyncLoading(false);
          setLastSync(syncData);
          notify.success("Sync complete");
        }
      }, 3000);
      setTimeout(() => {
        clearInterval(pollInterval);
        setManualSyncLoading(false);
      }, 10 * 60 * 1000);
    } catch (error) {
      if (error?.response?.status === 409) {
        notify.error("A sync is already in progress. Please wait.");
      } else {
        notify.error(getApiErrorMessage(error, "Could not sync data"));
        setManualSyncLoading(false);
      }
    }
  };

  const handleDisconnect = async () => {
    try {
      await AtriumApi.delete("/v1/church-metrics/disconnect");
      notify.success("Disconnected from Church Metrics");
      onStatusChange();
      return true;
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not disconnect from Church Metrics"));
      return false;
    }
  };

  const syncRow = (field, label) => (
    <SettingToggleRow
      label={label}
      checked={syncSettings[field]}
      onChange={() => handleToggleSync(field)}
    />
  );

  return (
    <IntegrationPanel
      logo={<ChurchMetricsLogo />}
      title="Church Metrics"
      description="Track attendance, visitor data and growth metrics to understand your church's health and engagement trends."
      connected={isConnected}
      expanded={expanded}
      onToggle={() => setExpanded((prev) => !prev)}
    >
      {!isConnected && (
        <SettingsSubsection
          title="Connect"
          description="Enter your Church Metrics credentials to connect your account."
        >
          <form onSubmit={handleConnect} noValidate className="flex flex-col gap-4 md:max-w-md">
            <InputField
              label="Email"
              required
              type="email"
              placeholder="your@email.com"
              value={credentials.email}
              error={credErrors.email}
              onChange={(e) => {
                setCredentials({ ...credentials, email: e.target.value });
                if (credErrors.email) setCredErrors({ ...credErrors, email: "" });
              }}
            />
            <InputField
              label="API Key"
              required
              placeholder="Enter your Church Metrics API key"
              value={credentials.apiKey}
              error={credErrors.apiKey}
              onChange={(e) => {
                setCredentials({ ...credentials, apiKey: e.target.value });
                if (credErrors.apiKey) setCredErrors({ ...credErrors, apiKey: "" });
              }}
            />
            <SettingsFormActions>
              <Button type="submit" loading={connectLoading}>
                {connectLoading ? "Connecting…" : "Connect"}
              </Button>
            </SettingsFormActions>
          </form>
        </SettingsSubsection>
      )}

      {isConnected && (
        <>
          <SettingsSubsection
            title="Sync Configuration"
            description="Choose which Church Metrics data to sync with Atrium."
          >
            {syncLoading ? (
              <Skeleton lines={5} />
            ) : (
              <>
                <SyncGroup title="Attendance Data">
                  {syncRow("attendanceRecords", "Sync attendance records to reports")}
                  {syncRow("serviceRecords", "Sync service records")}
                  {syncRow("visitorData", "Sync visitor and guest data")}
                  {syncRow("growthMetrics", "Sync growth metrics and trends")}
                </SyncGroup>
                <SyncGroup title="Automatic Sync">
                  {syncRow("autoSync", "Enable automatic sync")}
                </SyncGroup>
                {syncSettings.autoSync && (
                  <Select
                    label="Sync Frequency"
                    className="mt-4 w-full md:max-w-xs"
                    options={SYNC_FREQUENCY_OPTIONS}
                    value={syncSettings.syncFrequency}
                    onChange={(e) => handleFrequencyChange(e.target.value)}
                  />
                )}
                <AutosaveHint />
              </>
            )}
          </SettingsSubsection>

          <ManualSyncSection
            lastSyncAt={lastSync?.updatedAt}
            stats={[{ label: "Records Synced", value: lastSync?.recordCount }]}
            loading={manualSyncLoading}
            onSync={handleManualSync}
          />

          <DisconnectSection
            integration="Church Metrics"
            description="Disconnect the Church Metrics integration. Your existing data will be preserved."
            onDisconnect={handleDisconnect}
          />
        </>
      )}
    </IntegrationPanel>
  );
};

const IntegrationSettings = () => {
  const [loading, setLoading] = useState(true);
  const [integrationStatus, setIntegrationStatus] = useState({
    planningCenter: null,
    churchMetrics: null,
  });

  useEffect(() => {
    track(EVENTS.SETTINGS_INTEGRATION_VIEWED);
    fetchIntegrationStatus();

    // Returning from the Planning Center OAuth flow
    const params = new URLSearchParams(window.location.search);
    if (params.get("pc_connected") === "true") {
      notify.success("Connected to Planning Center");
    } else if (params.get("pc_error") === "true") {
      notify.error("Could not connect to Planning Center. Please try again.");
    }
  }, []);

  const fetchIntegrationStatus = async () => {
    setLoading(true);
    try {
      const [pcResponse, cmResponse] = await Promise.allSettled([
        AtriumApi.get("/v1/connect"),
        AtriumApi.get("/v1/church-metrics/status"),
      ]);

      setIntegrationStatus({
        planningCenter: pcResponse.status === "fulfilled" ? pcResponse.value?.data : null,
        churchMetrics: cmResponse.status === "fulfilled" ? cmResponse.value?.data : null,
      });
    } catch (error) {
      console.error("Failed to fetch integration status:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col gap-5">
        <SettingsSectionSkeleton lines={2} />
        <SettingsSectionSkeleton lines={2} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <PlanningCenterCard
        integrationStatus={integrationStatus}
        onStatusChange={fetchIntegrationStatus}
      />
      <ChurchMetricsCard
        integrationStatus={integrationStatus}
        onStatusChange={fetchIntegrationStatus}
      />
    </div>
  );
};

export default IntegrationSettings;
